import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Image } from '@tiptap/extension-image';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Node, mergeAttributes } from '@tiptap/core';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Highlighter, Link as LinkIcon, Undo, Redo, Unlink,
  Heading1, Heading2, Heading3, Quote, Minus,
  Table as TableIcon, Trash2
} from "lucide-react";

// Custom PageBreak node extension - renders <hr class="page-break-marker">
const PageBreak = Node.create({
  name: 'pageBreak',
  group: 'block',
  atom: true,
  
  parseHTML() {
    return [
      { tag: 'hr.page-break-marker' },
      { tag: 'hr[class="page-break-marker"]' },
    ];
  },
  
  renderHTML({ HTMLAttributes }) {
    return ['hr', mergeAttributes(HTMLAttributes, { class: 'page-break-marker' })];
  },
});

// Toolbar button component
const ToolbarButton = ({ onClick, isActive, disabled, title, children, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`
      h-7 w-7 flex items-center justify-center rounded transition-colors
      ${isActive ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' : 'hover:bg-slate-100 dark:hover:bg-slate-700'}
      ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
      ${className}
    `}
  >
    {children}
  </button>
);

// Toolbar divider
const ToolbarDivider = () => (
  <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />
);

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL del enlace:', previousUrl);
    
    if (url === null) return;
    
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="bg-[#f3f3f3] dark:bg-slate-800 border-b border-slate-300 dark:border-slate-600">
      {/* Tabs like Word */}
      <div className="flex items-center px-2 py-1 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <span className="text-xs font-medium text-slate-600 dark:text-slate-400 px-3 py-1 border-b-2 border-blue-500">
          Inicio
        </span>
      </div>
      
      {/* Main toolbar */}
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5">
        <ToolbarDivider />

        {/* Text formatting */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Negrita (Ctrl+B)"
        >
          <Bold className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Cursiva (Ctrl+I)"
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Subrayado (Ctrl+U)"
        >
          <UnderlineIcon className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title="Tachado"
        >
          <Strikethrough className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHighlight({ color: '#ffff00' }).run()}
          isActive={editor.isActive('highlight')}
          title="Resaltar"
        >
          <Highlighter className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Headings */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Título 1"
        >
          <Heading1 className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Título 2"
        >
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          title="Título 3"
        >
          <Heading3 className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Lists */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Lista con viñetas"
        >
          <List className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Lista numerada"
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Alignment */}
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          title="Alinear a la izquierda"
        >
          <AlignLeft className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          title="Centrar"
        >
          <AlignCenter className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          title="Alinear a la derecha"
        >
          <AlignRight className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          isActive={editor.isActive({ textAlign: 'justify' })}
          title="Justificar"
        >
          <AlignJustify className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Other elements */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title="Cita"
        >
          <Quote className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Línea horizontal"
        >
          <Minus className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={setLink}
          isActive={editor.isActive('link')}
          title="Insertar enlace"
        >
          <LinkIcon className="h-4 w-4" />
        </ToolbarButton>

        {editor.isActive('link') && (
          <ToolbarButton
            onClick={() => editor.chain().focus().unsetLink().run()}
            title="Quitar enlace"
          >
            <Unlink className="h-4 w-4" />
          </ToolbarButton>
        )}

        <ToolbarDivider />

        {/* Undo/Redo */}
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Deshacer (Ctrl+Z)"
        >
          <Undo className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Rehacer (Ctrl+Y)"
        >
          <Redo className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Table controls */}
        <ToolbarButton
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          title="Insertar tabla"
        >
          <TableIcon className="h-4 w-4" />
        </ToolbarButton>

        {editor.isActive('table') && (
          <>
            <ToolbarButton
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              title="Agregar columna"
            >
              <span className="text-[10px] font-bold">+Col</span>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().addRowAfter().run()}
              title="Agregar fila"
            >
              <span className="text-[10px] font-bold">+Fila</span>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().deleteColumn().run()}
              title="Eliminar columna"
            >
              <span className="text-[10px] font-bold text-red-500">-Col</span>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().deleteRow().run()}
              title="Eliminar fila"
            >
              <span className="text-[10px] font-bold text-red-500">-Fila</span>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().deleteTable().run()}
              title="Eliminar tabla"
              className="text-red-500"
            >
              <Trash2 className="h-4 w-4" />
            </ToolbarButton>
          </>
        )}
      </div>
    </div>
  );
};

// Ruler component (like Word)
const Ruler = () => (
  <div className="h-6 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 flex items-end px-4">
    <div className="flex-1 h-4 relative">
      {[...Array(17)].map((_, i) => (
        <div key={i} className="absolute bottom-0" style={{ left: `${i * 6.25}%` }}>
          <div className="h-2 w-px bg-slate-400" />
          <span className="text-[8px] text-slate-500 absolute -left-1">{i}</span>
        </div>
      ))}
    </div>
  </div>
);

export default function WordStyleEditor({ 
  content, 
  onChange, 
  editable = true, 
  minHeight = "500px",
  placeholder = "Haga clic aquí para comenzar a escribir...",
  headerHtml = "",
  footerHtml = ""
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        horizontalRule: false,
      }),
      Underline,
      TextStyle,
      Color,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Highlight.configure({
        multicolor: true,
      }),
      Link.configure({
        openOnClick: false,
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'docx-table',
        },
      }),
      TableRow,
      TableCell,
      TableHeader,
      Image.configure({
        HTMLAttributes: {
          class: 'docx-image',
        },
      }),
      PageBreak,
    ],
    content: content || '',
    editable,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
  });

  // Update content when it changes externally
  React.useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  // Update editable mode
  React.useEffect(() => {
    if (editor) {
      editor.setEditable(editable);
    }
  }, [editable, editor]);

  // Count pages based on page-break nodes in the editor
  const pageCount = React.useMemo(() => {
    if (!editor) return 1;
    const html = editor.getHTML();
    const breaks = (html.match(/page-break-marker/g) || []).length;
    return breaks + 1;
  }, [editor, content]);

  return (
    <div className="flex flex-col h-full bg-slate-100 dark:bg-slate-950 rounded-lg overflow-hidden border border-slate-300 dark:border-slate-700">
      {/* Toolbar */}
      {editable && <MenuBar editor={editor} />}
      
      {/* Ruler */}
      <Ruler />
      
      {/* Document area - simulating Word's page view */}
      <div className="flex-1 overflow-auto p-8 bg-slate-200 dark:bg-slate-900">
        {/* Header block (read-only, shown above editor) */}
        {headerHtml && (
          <div 
            className="mx-auto bg-white dark:bg-slate-800 shadow-lg rounded-t-sm border-b-0"
            style={{ maxWidth: '816px' }}
          >
            <div 
              className="doc-header-footer px-[72px] pt-[36px] pb-[12px]"
              dangerouslySetInnerHTML={{ __html: headerHtml }}
            />
            <hr className="mx-[72px] border-slate-300" />
          </div>
        )}

        {/* Editor Content */}
        <div 
          className="mx-auto bg-white dark:bg-slate-800 shadow-lg"
          style={{ 
            maxWidth: '816px',
            minHeight: minHeight,
            borderRadius: headerHtml ? '0' : '0.125rem 0.125rem 0 0',
          }}
        >
          <EditorContent 
            editor={editor} 
            className="word-editor-content"
          />
        </div>

        {/* Footer block (read-only, shown below editor) */}
        {footerHtml && (
          <div 
            className="mx-auto bg-white dark:bg-slate-800 shadow-lg rounded-b-sm"
            style={{ maxWidth: '816px' }}
          >
            <hr className="mx-[72px] border-slate-300" />
            <div 
              className="doc-header-footer px-[72px] pt-[12px] pb-[36px]"
              dangerouslySetInnerHTML={{ __html: footerHtml }}
            />
          </div>
        )}
      </div>

      {/* Status bar like Word */}
      <div className="h-6 bg-[#f3f3f3] dark:bg-slate-800 border-t border-slate-300 dark:border-slate-600 flex items-center justify-between px-3 text-xs text-slate-600 dark:text-slate-400">
        <div className="flex items-center gap-4">
          <span>{pageCount} página{pageCount !== 1 ? 's' : ''}</span>
          <span>{editor?.storage?.characterCount?.characters?.() || 0} caracteres</span>
        </div>
        <div className="flex items-center gap-2">
          <span>100%</span>
        </div>
      </div>

      <style>{`
        /* Header/Footer display styles */
        .doc-header-footer {
          font-family: 'Calibri', 'Arial', sans-serif;
          font-size: 9pt;
          line-height: 1.3;
        }
        .doc-header-footer table {
          border-collapse: collapse;
          width: 100%;
        }
        .doc-header-footer td, .doc-header-footer th {
          border: 1px solid #bbb;
          padding: 3px 6px;
          vertical-align: middle;
          text-align: center;
          font-size: 8pt;
        }
        .doc-header-footer img {
          max-height: 55px;
          display: block;
          margin: 0 auto;
        }
        .doc-header-footer div {
          line-height: 1.2;
        }

        /* Editor content styles */
        .word-editor-content {
          font-family: 'Calibri', 'Arial', sans-serif;
          font-size: 11pt;
          line-height: 1.5;
          padding: 72px 72px;
          min-height: ${minHeight};
        }
        
        .word-editor-content .ProseMirror {
          outline: none;
          min-height: calc(${minHeight} - 144px);
        }
        
        .word-editor-content .ProseMirror p {
          margin: 0 0 0.5em 0;
        }
        
        .word-editor-content .ProseMirror h1 {
          font-size: 24pt;
          font-weight: bold;
          color: #2b579a;
          margin: 0.5em 0;
        }
        
        .word-editor-content .ProseMirror h2 {
          font-size: 18pt;
          font-weight: bold;
          color: #2b579a;
          margin: 0.5em 0;
        }
        
        .word-editor-content .ProseMirror h3 {
          font-size: 14pt;
          font-weight: bold;
          color: #1f3763;
          margin: 0.5em 0;
        }
        
        .word-editor-content .ProseMirror p.is-editor-empty:first-child::before {
          color: #999;
          content: '${placeholder}';
          float: left;
          height: 0;
          pointer-events: none;
          font-style: italic;
        }
        
        .word-editor-content .ProseMirror ul,
        .word-editor-content .ProseMirror ol {
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        
        .word-editor-content .ProseMirror li {
          margin: 0.25em 0;
        }
        
        .word-editor-content .ProseMirror blockquote {
          border-left: 4px solid #2b579a;
          padding-left: 1em;
          margin: 1em 0;
          color: #666;
          font-style: italic;
        }
        
        .word-editor-content .ProseMirror hr {
          border: none;
          border-top: 1px solid #ccc;
          margin: 1em 0;
        }

        /* Page break marker style */
        .word-editor-content .ProseMirror hr.page-break-marker,
        .word-editor-content .ProseMirror hr[class="page-break-marker"] {
          border: none;
          border-top: 2px dashed #2b579a;
          margin: 2em 0;
          position: relative;
          page-break-after: always;
        }
        .word-editor-content .ProseMirror hr.page-break-marker::after {
          content: 'Salto de página';
          position: absolute;
          top: -10px;
          left: 50%;
          transform: translateX(-50%);
          background: white;
          padding: 0 8px;
          font-size: 8pt;
          color: #2b579a;
          letter-spacing: 1px;
          text-transform: uppercase;
        }
        
        .word-editor-content .ProseMirror a {
          color: #0563c1;
          text-decoration: underline;
        }
        
        .word-editor-content .ProseMirror mark {
          background-color: #ffff00;
          padding: 0 2px;
        }
        
        .word-editor-content .ProseMirror code {
          background-color: #f4f4f4;
          padding: 2px 4px;
          border-radius: 2px;
          font-family: 'Courier New', monospace;
          font-size: 10pt;
        }
        
        .word-editor-content .ProseMirror pre {
          background-color: #1e1e1e;
          color: #d4d4d4;
          padding: 1em;
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          font-size: 10pt;
          overflow-x: auto;
        }

        /* Table styles */
        .word-editor-content .ProseMirror table {
          border-collapse: collapse;
          width: 100%;
          margin: 0.5em 0;
          table-layout: auto;
        }
        
        .word-editor-content .ProseMirror table td,
        .word-editor-content .ProseMirror table th {
          border: 1px solid #999;
          padding: 6px 8px;
          vertical-align: top;
          min-width: 40px;
          font-size: 10pt;
        }
        
        .word-editor-content .ProseMirror table th {
          font-weight: bold;
          background-color: #f0f0f0;
        }
        
        .word-editor-content .ProseMirror table td p,
        .word-editor-content .ProseMirror table th p {
          margin: 0;
        }
        
        .word-editor-content .ProseMirror table .selectedCell {
          background-color: #d4e6ff;
        }

        /* Image styles */
        .word-editor-content .ProseMirror img {
          max-width: 100%;
          height: auto;
          margin: 0.5em 0;
        }

        /* Dark mode adjustments */
        .dark .word-editor-content .ProseMirror h1,
        .dark .word-editor-content .ProseMirror h2 {
          color: #6b9ed9;
        }
        
        .dark .word-editor-content .ProseMirror h3 {
          color: #8bb3e0;
        }
        
        .dark .word-editor-content .ProseMirror a {
          color: #5c9eff;
        }

        .dark .word-editor-content .ProseMirror hr.page-break-marker::after {
          background: #1e293b;
          color: #6b9ed9;
        }
      `}</style>
    </div>
  );
}
